import { Component, ViewChild } from '@angular/core';
import {
  AbstractBlModal,
  BlDialogService,
  BlReferenceGridComponent,
  ModalReason,
  ModalOption,
  ModalSize,
} from '@blcloud/bl-ng-ui-component';
import {
  IBizNegoInfo,
  IReceptionInfo,
  IWebReserveLinkReq,
  IItemServiceSetting,
  IVehicleInfo,
  VehicleInfo,
  ICustomerInfo,
  CustomerInfo
} from '@blcloud/bl-datamodel';
import { filter, map, switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs/Observable';
import { forkJoin as ObservableForkJoin } from 'rxjs/observable/forkJoin';
import { BlApiConditionHelper, BlApiSearchCondition, BlConditionType } from '@blcloud/bl-ng-common';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';
import { GridOptions } from 'ag-grid';
import { WebReserveReceptionLinkService } from './web-reserve-reception-link.service';
import { ColumnDefs } from './web-reserve-reception-link.define';
import {
  assign as _assign,
  defer as _defer,
  filter as _filter,
  find as _find,
  findIndex as _findIndex,
  map as _map,
  size as _size,
  isNil as _isNil,
} from 'lodash';
import * as _moment from 'moment';
import {
  ReceptionInfoResource,
  ItemServiceSettingResource,
  VehicleInfoResource,
  CustomerInfoResource
} from '@blcloud/bl-ng-resource';
import { AsyncSubject } from 'rxjs/AsyncSubject';
import {
  IGridData,
} from '@blcloud/bl-ng-share-module/feature/reception-list-modal/reception-list-modal.component';
import { DateTimeUtils, StringUtils } from '@blcloud/bl-common';
import { BizNegoStatus } from '@blcloud/bl-datamodel/enum/deals/biz-nego-status';
import { finalize } from 'rxjs/operators';

/**
 * Web予約受付紐付モーダル
 *
 */
@Component({
  selector: 'app-web-reserve-reception-link',
  templateUrl: './web-reserve-reception-link.component.html',
  styleUrls: ['./web-reserve-reception-link.component.scss']
})
export class WebReserveReceptionLinkComponent extends AbstractBlModal {

  /** 参照用グリッド */
  @ViewChild('refGrid') refGrid: BlReferenceGridComponent;

  /** OKボタン非活性 */
  _disableOkButton = true;

  /** thead / tr/ th / お客様名の幅 */
  _customerNameWidth = '16%';

  /** thead / tr/ th / フリガナの幅 */
  _customerKanaNameWidth = '16%';

  /** thead / tr/ th / 連絡先の幅 */
  _contactTelInformationWidth = '16%';

  /** thead / tr/ th / メールアドレスの幅 */
  _contactEmailInformationWidth = '16%';

  /** thead / tr/ th / 車両登録番号の幅 */
  _dispNumberPlateWidth = '16%';

  /** thead / tr/ th / メーカーの幅 */
  _vehicleNameWidth = '16%';

  /** グリッドオプション */
  _gridOptions: GridOptions;

  /** グリッドページあたりの件数 */
  _gridSizePatterns: number[] = [];

  /** 詳細グリッドヘッダの高さ */
  _gridHeaderHeight = 37;

  /** 詳細グリッドの高さ */
  _gridHeight = '100%';

  /** 詳細グリッド行の高さ */
  _gridRowHeight = 35;

  /** 検索結果件数 */
  _totalCount = 0;

  /** 選択データ */
  private selectedData = null;

  /** 受付情報が「商談中」のみのリスト */
  private adjustReceptionInfoList: IReceptionInfo[];

  private readonly onReadyGridSubject = new AsyncSubject<boolean>();

  /** 顧客情報 */
  customerInfo: ICustomerInfo;

  /** 車両情報 */
  vehicleInfo: IVehicleInfo;

  gridData: IGridData[];

  /** 商品サービス設定 */
  itemServiceSettingList: IItemServiceSetting[];
  itemServiceName = '';

  /** 顧客車両情報ヘッダ表示データ */
  _customerVehicleInfo$: Observable<{
    customerName: string,
    customerNameKana: string,
    contact: string,
    vehicleName: string,
  }>;

  /** Web予約確定メッセージ */
  private readonly DONE_MESSAGE = 'Web予約を確定しました。';

  /**
   * コンストラクタ.
   */
  constructor(
    private service: WebReserveReceptionLinkService,
    private dialogService: BlDialogService,
    private receptionInfoResource: ReceptionInfoResource,
    private itemServiceSettingResource: ItemServiceSettingResource,
    private customerInfoResource: CustomerInfoResource,
    private vehicleInfoResource: VehicleInfoResource,
  ) {
    super();
    this.initGrid();
  }

  /** @override */
  defaultOption(): ModalOption {
    return { animated: true, size: ModalSize.Full };
  }

  /**
   * @override
   */
  getResults(): any {
    return {};
  }

  /**
   * 表示後初期化処理
   */
  onShown() {
    const receptionInfoList$: Observable<IReceptionInfo[]> = this.receptionList$;
    this.onReadyGridSubject
      .pipe(
        filter(ready => ready),
        switchMap(() => ObservableForkJoin(
          receptionInfoList$,
          this.itemServiceSettingList$,
          this.customerInfo$,
          this.vehicleInfo$,
        )),
      )
      .subscribe(([receptionInfoList, itemServiceSettingList, customerInfo, vehicleInfo]) => {
        if ( _size(receptionInfoList) !== 0 ) {
          // OKボタン活性
          this._disableOkButton = false;
        }

        // 受付情報が「商談中」のみのリストにする
        this.adjustReceptionInfoList = _filter(receptionInfoList, data => {
          return data.bizNegoInfoList[0].bizNegoStatus === BizNegoStatus.During;
        });
        // 顧客車両情報ヘッダを初期化
        this.initCustomerVehicleHeader(customerInfo, vehicleInfo);

        this.itemServiceSettingList = itemServiceSettingList;
        this.customerInfo = customerInfo;
        this.vehicleInfo = vehicleInfo;
        this.loadData(this.adjustReceptionInfoList);
        this.hideLoading(this.refGrid);
      });
  }

  private loadData(receptionInfoList: IReceptionInfo[]) {
    if (receptionInfoList && receptionInfoList.length > 0) {
      receptionInfoList.sort((val1: IReceptionInfo, val2: IReceptionInfo) => {
        if (val1.updateDateTime > val2.updateDateTime) {
          return -1;
        }
        if (val1.updateDateTime < val2.updateDateTime) {
          return 1;
        }
        if (val1.receptionDate > val2.receptionDate) {
          return -1;
        }
        if (val1.receptionDate < val2.receptionDate) {
          return 1;
        }
        return 0;
      });
      this.gridData = _map(receptionInfoList, receptionInfo => {
        const bizNegoInfoIndex = _findIndex(receptionInfo.bizNegoInfoList, val => {
          return val.logicalDeleteDiv !== LogicalDeleteDiv.Deleted;
        });
        const dispInfo = {
          dispReceptionDate: '',
          dispUpdateDateTime: '',
          dispVisitStartDateTime: '',
          dispItemServiceName: '',
          dispItemServiceOfferStartDateTime: '',
          itemServiceColorString: '',
        };
        if (receptionInfo.receptionDate) {
          dispInfo.dispReceptionDate = DateTimeUtils.formatJapanEra(
            DateTimeUtils.parseDate(receptionInfo.receptionDate)
          );
        }
        if (receptionInfo.updateDateTime) {
          dispInfo.dispUpdateDateTime = DateTimeUtils.formatJapanEra(
            DateTimeUtils.parseDate(receptionInfo.updateDateTime)
          );
        }
        if (bizNegoInfoIndex !== -1) {
          const bizNegoInfo: IBizNegoInfo = receptionInfo.bizNegoInfoList[bizNegoInfoIndex];
          if (bizNegoInfo.visitStoreInfoList.length > 0 && bizNegoInfo.visitStoreInfoList[0].visitStartDateTime) {
            dispInfo.dispVisitStartDateTime = this.formatDateTime(
              _moment(bizNegoInfo.visitStoreInfoList[0].visitStartDateTime)
            );
          }
          if (bizNegoInfo.itemServiceOfferStartDateTime) {
            dispInfo.dispItemServiceOfferStartDateTime = this.formatDateTime(
              _moment(bizNegoInfo.itemServiceOfferStartDateTime)
            );
          }
          const itemServiceSetting: IItemServiceSetting = _find(this.itemServiceSettingList, val => {
            return val.itemServiceNum === bizNegoInfo.itemServiceNum;
          });
          if (itemServiceSetting) {
            dispInfo.itemServiceColorString = this.convertToColorString({
              red: itemServiceSetting.scheduleEventMst.eventColorPaletteInfoRedVal,
              green: itemServiceSetting.scheduleEventMst.eventColorPaletteInfoGreenVal,
              blue: itemServiceSetting.scheduleEventMst.eventColorPaletteInfoBlueVal,
              alpha: itemServiceSetting.scheduleEventMst.eventColorPaletteInfoAlphaVal,
            });
            dispInfo.dispItemServiceName = StringUtils.concat(
              '<span class="symbol" style="background-color: ', dispInfo.itemServiceColorString, '"></span>',
              bizNegoInfo.itemServiceName
            );
          } else {
            dispInfo.dispItemServiceName = bizNegoInfo.itemServiceName;
          }
        }
        return <IGridData>_assign({}, receptionInfo, dispInfo);
      });
      this.refGrid.setData(this.gridData);
      _defer(() => {
        this.refGrid.adjustSizeColumnToFit();
      });
    } else {
      this.gridData = [];
      this.refGrid.setData(this.gridData);
      this.refGrid.showNoRows();
    }

    this.clearSelectedData();
    const rowIndex = this._gridOptions.api.getFirstDisplayedRow();
    this._gridOptions.api.forEachNode(node => {
      if (node.rowIndex === rowIndex) {
        node.setSelected(true);
        this.setSelectedData(node.data);
      }
    });
  }

  private get itemServiceSettingList$(): Observable<IItemServiceSetting[]> {
    return this.itemServiceSettingResource
      .getAll<IItemServiceSetting>(
        {
          conditions: [
            { key: 'logicalDeleteDiv', value: LogicalDeleteDiv.Valid, type: BlConditionType.Equal },
          ],
          fields: [
            'itemServiceNum',
            'scheduleEventMst',
          ],
        }
      ).pipe(
        map(response => {
          if (_isNil(response)) {
            return [];
          }

          return response.searchResultList;
        }),
      );
  }

  private get customerInfo$(): Observable<ICustomerInfo> {
    return this.customerInfoResource.getById<ICustomerInfo>(new CustomerInfo({
      customerCode: +this.data.customerCode
    }).getIdString());
  }

  private get vehicleInfo$(): Observable<IVehicleInfo> {
    return this.vehicleInfoResource.getById<IVehicleInfo>(new VehicleInfo({
      vehicleMgtNumber: +this.data.vehicleMgtNumber
    }).getIdString());
  }

  private get receptionList$(): Observable<IReceptionInfo[]> {
    const conditions = this.data.conditions;
    const fields = [
      'blTenantId',
      'productCode',
      'receptOrganizationCode',
      'receptionNumPrefix',
      'receptionNum',
      'registerYearMonth',
      'logicalDeleteDiv',
      'customerCode',
      'vehicleMgtNumber',
      'receptionDate',
      'updateDateTime',
      'customerName1',
      'contactInfoTel',
      'bizNegoInfoList',
      'updateCtrlId',
    ];
    if (conditions instanceof BlApiSearchCondition) {
      conditions.addCondition(BlApiConditionHelper.makeCondStrEqual('customerCode', this.data.customerCode));
      conditions.addCondition(BlApiConditionHelper.makeCondStrEqual('vehicleMgtNumber', this.data.vehicleMgtNumber));
    } else {
      conditions.push(
        { key: 'customerCode', value: this.data.customerCode, type: BlConditionType.Equal },
        { key: 'vehicleMgtNumber', value: this.data.vehicleMgtNumber, type: BlConditionType.Equal }
      );
    }
    return this.receptionInfoResource
      .getAll<IReceptionInfo>(
        {
          conditions,
          fields,
        }
      ).pipe(
        map(response => {
          if (_isNil(response)) {
            return [];
          }

          return response.searchResultList;
        }),
      );
  }

  /**
   * 行選択時のイベント動作
   */
  onSelectedRow(e: any) {
    this.selectedData = e.data;
  }

  /**
   * @override
   */
  dismiss(reason: ModalReason) {
    if (ModalReason.Done === reason) {
      this.onDone();
    } else {
      this.emitDismissEvent(reason);
    }
  }

  /**
   * 新規作成ボタン押下のイベント動作
   */
  onClickAdd(): void {
    // 決定ボタン押下時処理
    this.onDone(true);
 }

  /**
   * 決定ボタン押下時処理
   * @param isClickAdd 新規作成ボタンを押下したかどうか
   */
  onDone(isClickAdd: boolean = false) {
    let message;
    if (!isClickAdd) {
      // 選択した受付情報
      const targetReceptionInfo = _find(this.adjustReceptionInfoList, res =>
        this.selectedData.receptionNum === res.receptionNum);

        // Web予約の予約日時
        const eventStartDate = this.formatDateTime(_moment(this.data.webreserve.eventStartDate + 'T' +
          StringUtils.paddingLeft(this.data.webreserve.eventStartTime, 4).slice(0, 2) +
          ':' + StringUtils.paddingLeft(this.data.webreserve.eventStartTime, 4).slice(2)));

      let changeDateMessage;
      if (targetReceptionInfo.bizNegoInfoList[0].itemServiceOfferStartDateTime) {
        // 選択した受付の入庫日時
        const receptionDate = this.formatDateTime(_moment(targetReceptionInfo.bizNegoInfoList[0].itemServiceOfferStartDateTime));
        changeDateMessage = '受付情報と紐付けを行います。\n受付の入庫日時 ' + receptionDate +
          ' は\nWeb予約日時 ' + eventStartDate + ' に更新されます。よろしいですか？';
      } else {
        changeDateMessage = '受付情報と紐付けを行います。\n受付の入庫日時はWeb予約日時 ' + eventStartDate + ' で設定されます。よろしいですか？';
      }
      message = changeDateMessage;
    } else {
      message = '新規で受付情報を登録します。よろしいですか？';
    }

    this.dialogService.confirm(message).subscribe(
      ref => {
        if (ref.reason === ModalReason.Done) {
          // Web予約情報と受付情報の候補を紐付け
          this.linkWebReserve(isClickAdd);
        }
        ref.hide();
        // 参照用グリッドにフォーカスを当てる
        _defer(() => this.refGrid.focus());
      }
    );
  }

  /**
   * グリッドの準備完了時
   */
  onReadyGrid() {
    // リサイズ処理
    this.resizeGrid();

    this.refGrid.setColumnDefs(ColumnDefs);
    this.refGrid.adjustSizeColumnToFit();
    this.refGrid.showLoading();
    this.onReadyGridSubject.next(true);
    this.onReadyGridSubject.complete();
  }

  /**
   * ページング: ページサイズ変更時処理
   * @param size ページサイズ
   */
  onChangePageSize(size: number): void {
    this._gridOptions.paginationPageSize = size;
    this.refGrid.gotoFirstPage();
  }

  /**
   * グリッド初期化
   */
  private initGrid(): void {
    this._gridOptions = <GridOptions>{};
    this._gridOptions.columnDefs = ColumnDefs;
    this._gridOptions.rowData = [];
    this._gridOptions.paginationPageSize = 50;
    this._gridSizePatterns = [50, 100, 150, 200];
  }

  /**
   * Web予約情報と受付情報の候補を紐付ける
   * @param isClickAdd 新規作成ボタンを押下したかどうか
   */
  private linkWebReserve(isClickAdd: boolean = false): void {
    let targetReceptionInfo;
    if (!isClickAdd) {
      // 紐付け候補一覧から選択中の顧客のものを検索
      targetReceptionInfo = _find(this.adjustReceptionInfoList, res =>
        this.selectedData.receptionNum === res.receptionNum);
    }

    const webReserveLinkReq: IWebReserveLinkReq = {};
    webReserveLinkReq.blTenantId = !isClickAdd ? targetReceptionInfo.blTenantId : this.data.webreserve.webReserveBlTenantId;
    webReserveLinkReq.webReserve = this.data.webreserve;
    webReserveLinkReq.productCode = !isClickAdd ? targetReceptionInfo.productCode : this.data.productCode;
    webReserveLinkReq.customerInfo = this.customerInfo;
    webReserveLinkReq.vehicleInfo = this.vehicleInfo;
    webReserveLinkReq.receptionInfo = !isClickAdd ? targetReceptionInfo : null;

    // ローディング開始
    this.showLoading();

    this.service.linkWebReserve(webReserveLinkReq).pipe(
      finalize(() => {
        // ローディング終了
        this.hideLoading();
      })
    ).subscribe(result => {
      if (result !== undefined) {
        this.dialogService.info(this.DONE_MESSAGE)
        .subscribe(blModalRef => {
          blModalRef.hide();
          this.emitDismissEvent(ModalReason.Done);
        });
      }
    });
  }

  private formatDateTime(dateTime: _moment.Moment) {
    return StringUtils.concat(
      DateTimeUtils.formatJapanEra(Number(dateTime.format('YYYYMMDD'))),
      ' ',
      dateTime.format(DateTimeUtils.formatString.shotTime)
    );
  }

  private convertToColorString(data: {
    red: number;
    green: number;
    blue: number;
    alpha: number;
  }): string {

    const red = !_isNil(data.red) ? data.red : 0;
    const green = !_isNil(data.green) ? data.green : 0;
    const blue = !_isNil(data.blue) ? data.blue : 0;
    const alpha = !_isNil(data.alpha) ? data.alpha : 1;

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }

  private clearSelectedData() {
    this.selectedData = null;
  }

  private setSelectedData(data) {
    this.selectedData = data;
  }

  /**
   * 顧客車両情報ヘッダ初期化
   * @param customerInfo
   * @param vehicleInfo
   */
  private initCustomerVehicleHeader(customerInfo: ICustomerInfo, vehicleInfo: IVehicleInfo) {
    this._customerVehicleInfo$ = this.service.getCustomerVehicleInfo(customerInfo, vehicleInfo);
  }

  /**
   * リサイズ処理
   */
  private resizeGrid(): void {
    // グリッド枠いっぱいに拡大
    this._gridOptions.api.sizeColumnsToFit();
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/web-reserve-reception-link/web-reserve-reception-link.component.ts